import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import PublicFooter from '../components/PublicFooter';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="flex items-center space-x-2">
            <MessageSquare className="h-8 w-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">ChatFlow</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service — ChatFlow</h1>
          <p className="text-gray-500 mb-8">Last updated: 2026-02-02</p>

          <div className="prose prose-gray max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              These Terms of Service ("Terms") govern your access to and use of the ChatFlow website and services (the "Service"). By creating an account or using the Service, you agree to these Terms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">1. The Service</h2>
            <p className="text-gray-700 mb-6">
              ChatFlow provides tools to automate customer conversations and manage messaging workflows, including optional integrations with third-party platforms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">2. Eligibility</h2>
            <p className="text-gray-700 mb-6">
              You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">3. Accounts and Security</h2>
            <p className="text-gray-700 mb-4">You are responsible for:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Providing accurate information when creating an account.</li>
              <li>Maintaining the confidentiality of your login credentials.</li>
              <li>All activity that occurs under your account.</li>
            </ul>
            <p className="text-gray-700 mb-6">
              If you believe your account is compromised, contact <a href="mailto:contact@chatflow.info" className="text-blue-600 hover:underline">contact@chatflow.info</a>.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">4. Integrations and Third-Party Services</h2>
            <p className="text-gray-700 mb-6">
              If you connect third-party services (e.g., Meta platforms such as Facebook/Instagram), you authorize ChatFlow to access and process data as needed to provide the integration. Third-party services are governed by their own terms and policies, and ChatFlow is not responsible for third-party outages or changes.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Use the Service for unlawful, harmful, or abusive activities.</li>
              <li>Attempt to gain unauthorized access to systems or data.</li>
              <li>Interfere with or disrupt the Service (e.g., malware, excessive automated requests).</li>
              <li>Infringe intellectual property or privacy rights of others.</li>
            </ul>
            <p className="text-gray-700 mb-6">
              We may suspend or terminate accounts that violate these Terms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">6. Subscriptions, Trials, and Billing</h2>
            <p className="text-gray-700 mb-6">
              If the Service includes paid plans, you agree to pay applicable fees and taxes. Trials (if offered) may convert to paid plans unless canceled. Fees are generally non-refundable except where required by law. Specific pricing and plan details will be presented within the Service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 mb-6">
              ChatFlow and its licensors own all rights in the Service, including software, design, and trademarks. You retain ownership of content you submit to the Service, but you grant ChatFlow a limited license to process it solely to operate the Service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">8. Privacy</h2>
            <p className="text-gray-700 mb-6">
              Your use of the Service is also governed by our Privacy Policy at <Link to="/privacy" className="text-blue-600 hover:underline">/privacy</Link>.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">9. Disclaimers</h2>
            <p className="text-gray-700 mb-6">
              The Service is provided "as is" and "as available." To the maximum extent permitted by law, we disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee uninterrupted or error-free operation.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 mb-6">
              To the maximum extent permitted by law, ChatFlow will not be liable for indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or goodwill. Our total liability for any claim relating to the Service will not exceed the amount you paid to ChatFlow in the 3 months before the event giving rise to the claim (or £100 if you have not paid).
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">11. Termination</h2>
            <p className="text-gray-700 mb-6">
              You may stop using the Service at any time. We may suspend or terminate your access if you violate these Terms, if required by law, or to protect the Service and users. Upon termination, your right to use the Service ends.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">12. Changes to the Terms</h2>
            <p className="text-gray-700 mb-6">
              We may update these Terms from time to time. We will post the updated version and update the "Last updated" date. Continued use of the Service after changes means you accept the updated Terms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">13. Governing Law</h2>
            <p className="text-gray-700 mb-6">
              These Terms are governed by the laws of England and Wales, and courts located there will have jurisdiction, except where mandatory consumer laws apply.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">Contact</h2>
            <p className="text-gray-700 mb-6">
              Questions about these Terms: <a href="mailto:contact@chatflow.info" className="text-blue-600 hover:underline">contact@chatflow.info</a>
            </p>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default Terms;
